/**
 * RFC-091: Client Detail Page
 * View and manage a specific client organization
 */

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { trpc, trpcClient } from '@/lib/trpc';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@deepguide-ai/ui/card';
import { Button } from '@deepguide-ai/ui/button';
import { Badge } from '@deepguide-ai/ui/badge';
import { Input } from '@deepguide-ai/ui/input';
import { Label } from '@deepguide-ai/ui/label';
import { Progress } from '@deepguide-ai/ui/progress';
import { Alert, AlertDescription } from '@deepguide-ai/ui/alert';
import { Separator } from '@deepguide-ai/ui/separator';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@deepguide-ai/ui/breadcrumb';
import {
  Building2,
  Users,
  FileText,
  Calendar,
  AlertCircle,
  CheckCircle,
  Edit,
  ExternalLink,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@deepguide-ai/ui/dialog';
import MainHeader from '@/components/layout/MainHeader';
import { Main } from '@/components/main';

export const Route = createFileRoute('/(protected)/partner/clients/$clientId')({
  component: ClientDetailPage,
});

function ClientDetailPage() {
  const { clientId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditingLimits, setIsEditingLimits] = useState(false);
  const [newLimit, setNewLimit] = useState<number>(0);
  const [error, setError] = useState('');

  const clientOptions = trpc.partner.getClient.queryOptions({ clientOrgId: clientId });
  const client = useQuery(clientOptions);

  const workspaceUsageOptions = trpc.partner.getClientWorkspaceUsage.queryOptions({
    clientOrgId: clientId,
  });
  const workspaceUsage = useQuery(workspaceUsageOptions);

  const updateLimitsMutation = trpc.partner.updateClientLimits.mutationOptions();
  const updateLimits = useMutation({
    ...updateLimitsMutation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner'] });
      setIsEditingLimits(false);
      setError('');
    },
    onError: (err: any) => {
      setError(err.message || 'Failed to update limits');
    },
  });

  if (client.isLoading || workspaceUsage.isLoading) {
    return (
      <Main>
        <MainHeader showSidebarTrigger={false} />
        <div className='mx-auto box-content flex max-w-[1008px] flex-col space-y-6 px-6 pb-12'>
          <div className="flex items-center justify-center min-h-[400px]">
            <div className='text-muted-foreground flex items-center gap-2'>
              <div className='border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent' />
              Loading...
            </div>
          </div>
        </div>
      </Main>
    );
  }

  if (!client.data?.client) {
    return (
      <Main>
        <MainHeader showSidebarTrigger={false} />
        <div className='mx-auto box-content flex max-w-[1008px] flex-col space-y-6 px-6 pb-12'>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Client organization not found</AlertDescription>
          </Alert>
        </div>
      </Main>
    );
  }

  const clientData = client.data.client;
  const usage = workspaceUsage.data?.usage;
  const usagePercent = usage && usage.limit > 0 ? usage.percentage : 0;

  const handleUpdateLimits = () => {
    updateLimits.mutate({
      clientOrgId: clientId,
      allocatedWorkspaces: newLimit,
      reason: 'Manual update from partner dashboard',
    });
  };

  const handleVisitSite = async () => {
    try {
      // 标记跨页切换，便于新页面首帧展示遮罩
      sessionStorage.setItem('partner:switching', '1')

      // Get current partner org ID before clearing
      const currentOrgId = localStorage.getItem('auth:activeOrg')
        ? JSON.parse(localStorage.getItem('auth:activeOrg')!).id
        : null;

      // Store delegation context FIRST
      localStorage.setItem('partner:delegation', JSON.stringify({
        isDelegate: true,
        clientOrgId: clientId,
        clientOrgName: clientData.name,
        partnerOrgId: currentOrgId,
        timestamp: Date.now(),
      }));

      // Switch to client organization using our custom tRPC endpoint
      // This allows partner delegation and updates both DB and session cookie
      await trpcClient.organizations.setActiveOrganization.mutate({
        organizationId: clientId
      });

      // IMPORTANT: Clear session cache to force re-fetch from DB
      localStorage.removeItem('auth:session');

      // Clear ALL cached state
      localStorage.removeItem('auth:activeOrg');
      localStorage.removeItem('auth:activeWorkspace');
      localStorage.removeItem('auth:activeWorkspaceId');

      // Clear React Query cache
      queryClient.clear();

      // Use assign() for true full page reload (not cached)
      window.location.assign('/');
    } catch (error) {
      console.error('Failed to switch to client organization:', error);
      setError('Failed to access client organization');
    }
  };

  return (
    <Main>
      <MainHeader showSidebarTrigger={false}>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/partner">Partner Dashboard</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{clientData.name}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </MainHeader>
      <div className='mx-auto box-content flex max-w-[1008px] flex-col space-y-6 px-6 pb-12 pt-6'>
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <div className='text-foreground text-2xl leading-6 font-semibold'>{clientData.name}</div>
              <Badge>{clientData._count.workspaces > 0 ? 'Active' : 'Inactive'}</Badge>
            </div>
            <div className='text-muted-foreground text-sm leading-5'>
              Client since {format(new Date(clientData.createdAt), 'MMM d, yyyy')}
            </div>
          </div>
          {/* <Button variant="default" size="sm" onClick={handleVisitSite} className='gap-2'>
            <ExternalLink className="h-4 w-4" />
            Visit Site
          </Button> */}
        </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Overview Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Workspaces</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{clientData._count.workspaces}</div>
            <p className="text-xs text-muted-foreground">
              {usage && usage.limit > 0
                ? `${usage.used} / ${usage.limit} allocated`
                : 'Unlimited'
              }
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Team Members</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{clientData._count.members}</div>
            <p className="text-xs text-muted-foreground">
              Total users
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Plan</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{clientData.plan}</div>
            <p className="text-xs text-muted-foreground">
              Current plan
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Workspace Usage */}
      {/* {usage && usage.limit > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Workspace Usage</CardTitle>
                <CardDescription>
                  {usage.used} of {usage.limit} workspaces used
                </CardDescription>
              </div>
              <Dialog open={isEditingLimits} onOpenChange={setIsEditingLimits}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" onClick={() => setNewLimit(usage.limit)}>
                    <Edit className="mr-2 h-4 w-4" />
                    Update Limit
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Update Workspace Limit</DialogTitle>
                    <DialogDescription>
                      Modify the workspace allocation for {clientData.name}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="newLimit">New Workspace Limit</Label>
                      <Input
                        id="newLimit"
                        type="number"
                        min="0"
                        value={newLimit}
                        onChange={(e) => setNewLimit(parseInt(e.target.value, 10))}
                      />
                      <p className="text-sm text-muted-foreground">
                        Current usage: {usage.used} workspaces
                      </p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsEditingLimits(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleUpdateLimits} disabled={updateLimits.isPending}>
                      {updateLimits.isPending ? 'Updating...' : 'Update'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>{usagePercent}% utilized</span>
                <span className="text-muted-foreground">
                  {usage.remaining} remaining
                </span>
              </div>
              <Progress value={usagePercent} className="h-2" />
              {usagePercent >= 80 && (
                <Alert variant={usagePercent >= 100 ? 'destructive' : 'default'}>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {usagePercent >= 100
                      ? 'Workspace limit reached. Client cannot create more workspaces.'
                      : 'Client is approaching their workspace limit.'}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </CardContent>
        </Card>
      )} */}

      {/* Workspaces */}
      {clientData.workspaces && clientData.workspaces.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Workspaces ({clientData.workspaces.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {clientData.workspaces.map((workspace: any) => (
                <div key={workspace.id} className="flex items-center justify-between py-3 border-b last:border-b-0">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="font-medium">{workspace.title}</div>
                        <div className="text-sm text-muted-foreground">
                          {workspace.type} • Created {formatDistanceToNow(new Date(workspace.createdAt), { addSuffix: true })}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {workspace.enabledFrameworks && workspace.enabledFrameworks.length > 0 ? (
                      workspace.enabledFrameworks.map((framework: string) => (
                        <Badge key={framework} variant="secondary" className="text-xs">
                          {framework}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-muted-foreground">No frameworks</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Team Members */}
      <Card>
        <CardHeader>
          <CardTitle>Team Members ({clientData.members.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {clientData.members.map((member) => (
              <div key={member.id} className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-medium">{member.user.name || member.user.email}</div>
                    <div className="text-sm text-muted-foreground">{member.user.email}</div>
                  </div>
                </div>
                <Badge variant="outline">{member.role}</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      </div>
    </Main>
  );
}
